import { clsx } from "clsx";
import { type FC, useMemo } from "react";

import {
  ActionButton,
  ActionButtonIntent,
  ActionButtonSize,
  Dialog,
  HStack,
  SymbolIcon,
  TooltipIntent,
  VStack,
} from "widgets";

import style from "../../styles/Cells.module.scss";

import {
  type IActionWidgetPropsInjectedFromController,
  PopoverController,
  TooltipController,
} from "../../../overlays/components/OverlayControllers.js";
import { type IAgGridCellProps } from "../../cell-base-interfaces.js";
import { traverseAndCollect } from "../../column-props-factory.js";
import ListCellContent, { type ListCellItem } from "./ListCellContent.js";
import { TableCell, type TableCellProps } from "./TableCell.js";
import { type ProjectFile } from "@/graphql.ts";
import { FileTile } from "@/modules/files/components/FileTile.js";
import { isColumnExpanded } from "@/redux/modules/table-data.ts";
import { SUBROW_HEIGHT, nonBreakingSpace } from "@/settings/settings.js";

const FileCell: FC<IAgGridCellProps & Pick<TableCellProps, "triggerButtonProps">> = (props) => {
  if (props.data == null) {
    return null;
  }

  const { data, colDef } = props;
  const value = traverseAndCollect(data, colDef.groupField || colDef.field);

  const cellExpanded = useMemo<boolean>(() => {
    return isColumnExpanded(props.colDef, props.context?.expansionPath);
  }, [props.context?.expansionPath]);

  const multiValues: ProjectFile[] = Array.isArray(value) ? value : [value];

  // a file cell can contain a ProjectFile (e.g. review screen of Playbook after attaching file to sample)
  const items: ListCellItem[] = multiValues.map((file: ProjectFile) => {
    if (!file) {
      return { value: nonBreakingSpace };
    }
    const projectFile = file;
    return {
      value: <FilePopover projectFile={projectFile} />,
    };
  });
  return (
    <TableCell triggerButtonProps={props.triggerButtonProps}>
      {cellExpanded ? (
        <ListCellContent items={items} colDef={colDef} style={{ height: SUBROW_HEIGHT }} context={props.context} />
      ) : (
        <FilesPopover projectFiles={multiValues} />
      )}
    </TableCell>
  );
};

const FilePopover: FC<{ projectFile: ProjectFile }> = (props) => {
  const projectFile = props.projectFile;
  return (
    <PopoverController
      actionWidget={(controllerProps) => (
        <MiniFileButton
          controllerProps={controllerProps}
          label={projectFile?.fileName}
          isDeleted={projectFile?.deleted}
          isHidden={projectFile?.hidden}
        />
      )}
    >
      {(controllerProps) => (
        <Dialog {...controllerProps}>
          <FileTile file={projectFile} />
        </Dialog>
      )}
    </PopoverController>
  );
};

const FilesPopover: FC<{ projectFiles: ProjectFile[] }> = (props) => {
  const projectFiles = props.projectFiles.filter((projectFile) => projectFile);

  if (!projectFiles?.length) {
    return null;
  }
  const label = projectFiles.length > 1 ? `${projectFiles.length} files` : projectFiles[0].fileName;
  const deletedIcon = projectFiles.every((projectFile) => projectFile.deleted);
  const hiddenIcon = projectFiles.every((projectFile) => projectFile.hidden);

  return (
    <PopoverController
      actionWidget={(controllerProps) => (
        <MiniFileButton controllerProps={controllerProps} label={label} isDeleted={deletedIcon} isHidden={hiddenIcon} />
      )}
    >
      {(controllerProps) => (
        <Dialog {...controllerProps}>
          <VStack>
            {projectFiles.map((file, i) => (
              <FileTile key={i} file={file} />
            ))}
          </VStack>
        </Dialog>
      )}
    </PopoverController>
  );
};

interface MiniFileButtonProps {
  label: string;
  controllerProps: IActionWidgetPropsInjectedFromController;
  isDeleted?: boolean;
  isHidden?: boolean;
}

const MiniFileButton: FC<MiniFileButtonProps> = (props) => {
  return (
    <ActionButton
      {...props.controllerProps}
      icon={"draft"}
      size={ActionButtonSize.InTableCell}
      intent={ActionButtonIntent.Outlined}
      aria-label={"Show more details"}
    >
      <HStack spacing={3}>
        {props.isDeleted || props.isHidden ? (
          <TooltipController
            content={props.isDeleted ? "This file has been deleted" : "Access to this file is restricted"}
            overlayOptions={{ offset: 12, delay: 600 }}
            intent={TooltipIntent.Danger}
          >
            {(tooltipTriggerProps) => (
              <div
                className={clsx(style.cell_file_tag, style.cell_file_tag_error, style.cell_file_tag_for_table)}
                onPointerEnter={tooltipTriggerProps.onPointerEnter}
                onPointerLeave={tooltipTriggerProps.onPointerLeave}
                ref={tooltipTriggerProps.ref}
              >
                <SymbolIcon
                  icon={props.isDeleted ? "delete" : "security"}
                  className={clsx(style.cell_file_tag, style.cell_file_tag_error)}
                />
              </div>
            )}
          </TooltipController>
        ) : null}
        <TooltipController content={props.label} overlayOptions={{ delay: 600, isDisabled: !props.label }}>
          {(tooltipTriggerProps) => (
            <div
              onPointerEnter={tooltipTriggerProps.onPointerEnter}
              onPointerLeave={tooltipTriggerProps.onPointerLeave}
              ref={tooltipTriggerProps.ref}
              style={{ minWidth: 0, overflow: "hidden" }}
            >
              {props.label}
            </div>
          )}
        </TooltipController>
      </HStack>
    </ActionButton>
  );
};

export default FileCell;
